/*
 * Copyright (c) 2021 LucidResolutions LLC. All rights reserved.
 */


import React, {useContext, useEffect, useState} from "react";
import UserService from "../../service/user.service";
import {
    Card,
    Col,
    Input,
    InputNumber,
    Layout,
    message,
    Modal,
    PageHeader, Result,
    Row,
    Skeleton,
    Steps,
    Switch,
    Tabs
} from "antd";
import {useWindowSize} from "../../hooks/windowSize";
import {IfEqual, IfTruthy} from "../../component/if.equal.component";
import {LoadingOutlined} from "@ant-design/icons";
import PaymentService from "../../service/payment.service";
import {AuthContext} from "../../context/auth.context";

const userSVC = new UserService()
const paymentSVC = new PaymentService()

const { TabPane } = Tabs
const { Step } = Steps

const SettingsPage = (props: any) =>{
    const authContext = useContext(AuthContext)
    const [userEmail, setUserEmail] = useState('')
    const [hasTwoFactor, setHasTwoFactor] = useState(false)
    const [loading, setLoading] = useState(true)
    const [showDisableTwoFactorModal, setShowDisableTwoFactorModal] = useState(false)
    const [twoFactorValue, setTwoFactorValue] = useState('')
    const [makingRequest, setMakingRequest] = useState(false)
    const [current2FAStep, setCurrent2FAStep] = useState(0)
    const [showEnable2FAModal, setShowEnable2FAModal] = useState(false)
    const [twoFactorImageSrc, set2FAImageSrc] = useState('')
    const [twoFactorSecret, set2FASecret] = useState('')
    const [generating2FA, setGenerating2FA] = useState(false)
    const [enabling2FA, setEnabling2FA] = useState(false)
    const [viewSecret, setViewSecret] = useState(false)
    const [activeTab, setActiveTab] = useState('')
    const [paymentButtons, setPaymentButtons] = useState([])
    const [override, setOverride] = useState(props?.location?.state?.billing ? 'billing' : undefined)

    let size = useWindowSize()

    useEffect(()=>{
        loadInfo().finally(()=>{
            setLoading(false)
        })

        // let id = setInterval(()=>{
        //     userSVC.info().then()
        // }, 1000)
        //
        // return ()=>{
        //     clearInterval(id)
        // }

    }, [])

    useEffect(()=>{
        if(override){
            setActiveTab(override)
        }
    }, [override])

    useEffect(() => {
        if(activeTab != "billing"){
            return
        }
        let id = setInterval(()=>{
            userSVC.info().then(()=>{})
            if(authContext.userStatus){
                clearInterval(id)
                setOverride(undefined)
            }
        }, 3000)

        return ()=> clearInterval(id)
    }, [activeTab, authContext.userStatus]);

    useEffect(() => {
        if(authContext.userStatus  || paymentButtons.length || activeTab != "billing"){
            return
        }


        paymentSVC.buttons().then((buttons)=>{
            setPaymentButtons(buttons)
        })
    }, [paymentButtons, authContext.userStatus, activeTab]);

    const onTwoFactorToggle = async (val: boolean)=>{
        if(!val){
            setShowDisableTwoFactorModal(true)
            return
        }

        setGenerating2FA(true)
        setCurrent2FAStep(0)
        setTwoFactorValue('')
        setShowEnable2FAModal(true)

        let errored = false
        try {
            let info = await userSVC.generateTwoFactor()
            set2FASecret(info.secret)
            set2FAImageSrc(info.image)
        }catch (e){
            errored = true
            message.error("An error occurred while generating a new two factor. try again")
        } finally{
            if(errored){
                setShowEnable2FAModal(false)
                return
            }

            setGenerating2FA(false)
        }

        console.log("two factor toggled", val)
    }

    const disableTwoFactor = async ()=>{
        setMakingRequest(true)
        try {
            let val = twoFactorValue?.replace(/[^\d]/g, '')
            if(val?.length != 6){
                message.error("you must enter 6 digits")
                return
            }

            await userSVC.disableTwoFactor(val)

            message.success(`Two Factor Disabled`)
            setShowDisableTwoFactorModal(false)
            setHasTwoFactor(false)
        }
        catch (e){
            message.error(`Invalid Code. Try again`)
        }
        finally {
            setMakingRequest(false)
        }



    }

    const loadInfo = async ()=>{
        setLoading(true)
        let info = await userSVC.info()
        setUserEmail(info.email)
        setHasTwoFactor(info.two_factor_enabled)
    }

    const onTabChange = (key: string)=>{
        if(key != override){
            setOverride(undefined)
        }
        setActiveTab(key)

        if(key == "billing" && authContext.userStatus){
            paymentSVC.getBillingPortal().then((res)=>{
                window.location = res.url
            })
        }

    }

    return <PageHeader title="Settings">
        <Skeleton active={true} loading={loading}>
            <Tabs defaultActiveKey={"profile"} size={'large'} type={'card'} onChange={onTabChange} activeKey={override}>
                <TabPane tab="Profile" key={"profile"}>
                    <Layout>
                        <Row gutter={[16,16]}>
                            <Col span={size.isSmall ? 24 : 12}>
                                <Card style={{height: 125}}>
                                    <Row>
                                        <Col span={20}>
                                            <h2>Account</h2>
                                            <Input disabled={true} value={userEmail} />
                                        </Col>
                                    </Row>

                                </Card>
                            </Col>
                            <Col span={size.isSmall ? 24 : 12}>
                                <Card style={{height: 125}}>
                                    <h2>Security</h2>
                                    Add an extra layer of Security using 2FA &nbsp;
                                    <Switch
                                        checked={hasTwoFactor}
                                        autoFocus={hasTwoFactor}
                                        checkedChildren="ON"
                                        unCheckedChildren="OFF"
                                        onChange={onTwoFactorToggle}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </Layout>
                </TabPane>

                <TabPane tab={"Billing"} key={"billing"}>
                    <Card>
                        <IfTruthy val={authContext.userStatus}>
                            <Result
                                icon={<LoadingOutlined />}
                                title="Settings Portal"
                                subTitle="Redirecting...."
                            />
                        </IfTruthy>

                        <IfTruthy val={!authContext.userStatus}>
                            {paymentButtons.map((b: any)=> {

                                // @ts-ignore
                                return <stripe-buy-button
                                    key={b.id}
                                    style={{marginRight: 10}}
                                    buy-button-id={b.id}
                                    publishable-key={b.key}
                                    customer-session-client-secret={b.client_secret}
                                    client-reference-id={b.reference_id}
                                />
                            })}
                        </IfTruthy>
                    </Card>
                </TabPane>
            </Tabs>
        </Skeleton>

        <Modal visible={showDisableTwoFactorModal}
               title={"Disable Two-Factor Authentication"}
               okText={makingRequest ? "Disabling..." : "Disable Two-Factor"}
               cancelText={"Cancel"}
               okButtonProps={{loading: makingRequest}}
               onCancel={()=>setShowDisableTwoFactorModal(false)}
               onOk={()=>disableTwoFactor().finally()}
        >
            <Row>
                <Col span={24}>
                    Enter the security code generated by your authenticator app. <br/><br/>
                    <Input
                        placeholder="6 digit code"
                        style={{width: "60%"}}
                        onChange={(e)=> setTwoFactorValue(e.target.value)}/>
                </Col>
            </Row>

        </Modal>

        <Modal
            title={"Two-Factor Authentication Setup"}
            visible={showEnable2FAModal}
            onCancel={()=> setShowEnable2FAModal(false)}
            width={'80%'}
            okText={
                current2FAStep == 0 ? "Next" : (current2FAStep == 1 ? "Submit" : "Done")
            }
            confirmLoading={enabling2FA}
            onOk={()=>{
                if(current2FAStep == 0){
                    setCurrent2FAStep(1)
                }else if(current2FAStep == 1){
                    setEnabling2FA(true)
                    userSVC.enableTwoFactor(twoFactorSecret, twoFactorValue).then(()=>{
                        setCurrent2FAStep(2)
                        setHasTwoFactor(true)
                    }).catch((e)=>{
                        console.log(e)
                        message.error("bad code entered try again")
                    }).finally(()=>{
                        setEnabling2FA(false)
                    })
                } else {
                    setShowEnable2FAModal(false)
                }
            }}
        >

            <Skeleton active={true} loading={generating2FA}>
                <Steps current={current2FAStep} onChange={(val) =>{
                    if(val < 2 && current2FAStep != 2){
                        setCurrent2FAStep(val)
                    }
                }}>
                    <Step title="Setup">Oh snap first step</Step>
                    <Step title="Verify"/>
                    <Step title="Complete" />
                </Steps>


                <Row gutter={[16,16]} style={{marginTop: 20}}>
                    <Col span={20} push={2}>
                        <IfEqual expect={0} actual={current2FAStep}>
                            <div className={'centered'}>
                                An authenticator app lets you generate security codes on your phone without needing to
                                receive text messages.

                                <br/>
                                <br/>
                                <IfTruthy val={!viewSecret}>
                                    Scan this code:
                                    <br/>
                                    <img src={twoFactorImageSrc}/>
                                </IfTruthy>
                                <IfTruthy val={viewSecret}>
                                    Secret: <br/>
                                    <span style={{
                                        display: "inline-block",
                                        marginTop: 10,
                                        marginBottom: 10,
                                        color: 'rgba(0, 0, 0, 0.40)',
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #d9d9d9',
                                        padding: '3px 7px'
                                    }}>{twoFactorSecret}</span>
                                </IfTruthy>


                                <br/>
                                <Switch checked={viewSecret} onChange={setViewSecret} checkedChildren={"View QR"} unCheckedChildren={"View Secret"}></Switch>
                            </div>

                        </IfEqual>

                        <IfEqual expect={1} actual={current2FAStep}>
                            Enter the security code generated by your authenticator app. <br/><br/>
                            <Input
                                placeholder="6 digit code"
                                style={{width: "60%"}}
                                onChange={(e)=> setTwoFactorValue(e.target.value)}/>
                        </IfEqual>

                        <IfEqual expect={2} actual={current2FAStep}>
                            <Result status={'success'} title={"Complete"} subTitle={"Your two-factor authentication setup is complete"}/>
                        </IfEqual>
                    </Col>
                </Row>
            </Skeleton>


        </Modal>
    </PageHeader>
}

export {SettingsPage}
